// Stockfish 17.1 lite (single-threaded WASM) running inside a Web Worker.
const ENGINE_URL = `${process.env.PUBLIC_URL || ""}/engine/stockfish-17.1-lite-single-03e3232.js`;
export const ENGINE_NAME = "Stockfish 17.1 lite (WASM)";
const LOAD_TIMEOUT_MS = 30000;
const HASH_MB = 32;

function parseInfo(line, acc) {
  const tok = line.split(" ");
  if (tok.includes("multipv") && tok[tok.indexOf("multipv") + 1] !== "1") return;
  const di = tok.indexOf("depth");
  const si = tok.indexOf("score");
  const pi = tok.indexOf("pv");
  if (di < 0 || si < 0) return;
  acc.depth = Number(tok[di + 1]);
  const kind = tok[si + 1];
  const val = Number(tok[si + 2]);
  acc.cp = kind === "cp" ? val : null;
  acc.mate = kind === "mate" ? val : null;
  if (pi >= 0) acc.pv = tok.slice(pi + 1);
}

class BrowserEngine {
  constructor() {
    this.worker = null;
    this.ready = null;
    this.chain = Promise.resolve();
    this.onLine = null;
  }

  _spawn() {
    this.worker = new Worker(ENGINE_URL);
    this.worker.onmessage = (e) => this.onLine && this.onLine(String(e.data));
    this.ready = new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("The chess engine took too long to load.")), LOAD_TIMEOUT_MS);
      this.worker.onerror = () => { clearTimeout(timer); reject(new Error("The chess engine couldn't start in this browser.")); };
      this.onLine = (line) => {
        if (line === "uciok") {
          this.worker.postMessage(`setoption name Hash value ${HASH_MB}`);
          this.worker.postMessage("isready");
        } else if (line === "readyok") {
          clearTimeout(timer);
          this.onLine = null;
          resolve();
        }
      };
      this.worker.postMessage("uci");
    });
    this.ready.catch(() => this._kill());
    return this.ready;
  }

  _kill() {
    if (this.worker) this.worker.terminate();
    this.worker = null;
    this.ready = null;
    this.onLine = null;
  }

  init() {
    return this.ready || this._spawn();
  }

  _run(fen, { movetime, depth }) {
    const timeoutMs = movetime ? movetime + 10000 : 90000;
    return new Promise((resolve, reject) => {
      const acc = { depth: 0, cp: null, mate: null, pv: [] };
      const timer = setTimeout(() => {
        this.onLine = null;
        this._abort = null;
        this._kill();
        reject(new Error("engine-timeout"));
      }, timeoutMs);
      this._abort = () => {
        clearTimeout(timer);
        this.onLine = null;
        this._abort = null;
        reject(new Error("engine-stopped"));
      };
      this.onLine = (line) => {
        if (line.startsWith("info ")) parseInfo(line, acc);
        else if (line.startsWith("bestmove")) {
          clearTimeout(timer);
          this.onLine = null;
          this._abort = null;
          const bm = line.split(" ")[1];
          if (!acc.pv.length && bm && bm !== "(none)") acc.pv = [bm];
          resolve(acc);
        }
      };
      this.worker.postMessage(`position fen ${fen}`);
      this.worker.postMessage(depth ? `go depth ${depth}` : `go movetime ${movetime}`);
    });
  }

  /** Side-to-move eval: {depth, cp, mate, pv[uci]}. Restarts the worker and retries once on failure. */
  analyse(fen, opts) {
    const job = this.chain.then(async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await this.init();
          return await this._run(fen, opts);
        } catch (e) {
          if (attempt === 1 || e.message === "engine-stopped") throw e;
        }
      }
      return null;
    });
    this.chain = job.catch(() => {});
    return job;
  }

  /** Abort any running search and shut the worker down; the next analyse() respawns it. */
  stop() {
    this.stopLive();
    if (this._abort) this._abort();
    this._kill();
    this.chain = Promise.resolve();
  }

  /** Stream deepening evals for one position via onUpdate({depth, cp, mate, pv}); a new call replaces the old one. */
  startLive(fen, maxDepth, onUpdate) {
    this.stopLive();
    const token = { cancelled: false, stop: null };
    this.liveToken = token;
    const job = this.chain.then(async () => {
      if (token.cancelled) return;
      await this.init();
      if (token.cancelled) return;
      await new Promise((resolve) => {
        const acc = { depth: 0, cp: null, mate: null, pv: [] };
        const finish = () => { clearTimeout(timer); this.onLine = null; resolve(); };
        const timer = setTimeout(() => { this.onLine = null; this._kill(); resolve(); }, 60000);
        token.stop = () => this.worker && this.worker.postMessage("stop");
        this.onLine = (line) => {
          if (line.startsWith("info ")) {
            const before = acc.depth;
            parseInfo(line, acc);
            if (!token.cancelled && acc.depth && acc.depth !== before) onUpdate({ ...acc });
          } else if (line.startsWith("bestmove")) finish();
        };
        this.worker.postMessage(`position fen ${fen}`);
        this.worker.postMessage(`go depth ${maxDepth}`);
      });
    });
    this.chain = job.catch(() => {});
  }

  stopLive() {
    if (!this.liveToken) return;
    this.liveToken.cancelled = true;
    if (this.liveToken.stop) this.liveToken.stop();
    this.liveToken = null;
  }
}

let instance = null;
export function getEngine() {
  if (!instance) instance = new BrowserEngine();
  return instance;
}
